import { FormGroup, FormParagraph } from '@ksyos/design-system';
import { isEmpty } from 'lodash';
import { Fragment, type JSX } from 'react';
import type { GenericData } from '@common/formEngine/generic/generic.types';
import type { ProcessActivityReadSectionItemType } from '@common/interfaces/processActivityReadSection';
import { CustomControlMode } from '@common/enums';
import { isGenericKey } from '@common/formEngine/generic/genericFields';
import { isProcessActivityAssetArray } from '@common/validation/processActivityAsset';
import { PatientInsurance } from '@frontend/components/pages/episodeCategory/customControls/patientInsurance/PatientInsurance';
import { ProcessActivityAssetsRead } from '@frontend/components/pages/episodeCategory/customControls/processActivityAssets/ProcessActivityAssetsRead';
import { useProcessActivityContext, useProcessActivityReadContext } from '@frontend/utils';
import type { ComponentsMap } from './readForm.types';

type ReadFormSectionItemProps = Readonly<{
    components?: ComponentsMap;
    item: ProcessActivityReadSectionItemType;
}>;

export function ReadFormSectionItem({ components, item }: ReadFormSectionItemProps): JSX.Element | null {
    const { serviceCode } = useProcessActivityContext();
    const { processActivity } = useProcessActivityReadContext<GenericData>();

    if (components?.has(item.key)) {
        return <Fragment key={item.key}>{components.get(item.key)}</Fragment>;
    }

    if (isProcessActivityAssetArray(item.value)) {
        return (
            <ProcessActivityAssetsRead
                assets={item.key === 'assets' ? item.value : item.value.filter((asset) => asset.category === item.key)}
                key={`${item.key}-${item.label}`}
                label={item.label}
            />
        );
    }

    // This is currently a condition for one generic field, but in the future a switch might be more applicable
    if (isGenericKey(item.key)) {
        return (
            <PatientInsurance
                isPatientInformed={Boolean(processActivity.customControlData.patient_verzekeringskosten_akkoord)}
                mode={CustomControlMode.read}
                serviceCode={serviceCode}
            />
        );
    }

    if (typeof item.value !== 'string' || isEmpty(item.value)) {
        return null;
    }

    return (
        <FormGroup
            key={item.key}
            label={item.label}
        >
            <FormParagraph>{item.value}</FormParagraph>
        </FormGroup>
    );
}
